import { useState, useEffect } from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useAuthStore } from '../../store/authStore'
import { useUserDefaultsStore } from '../../store/userDefaultsStore'
import { useLanguageStore } from '../../store/languageStore'
import { apiClient } from '../../api/client'
import { API_ENDPOINTS } from '../../api/endpoints'
import { secureStorage } from '../../utils/secureStorage'
import toast from 'react-hot-toast'
import {
  FiLock,
  FiUser,
  FiEye,
  FiEyeOff,
  FiDollarSign,
  FiPieChart,
  FiTrendingUp,
  FiFileText,
  FiPercent,
  FiCreditCard,
} from 'react-icons/fi'
import { useActivityLogger } from '../../hooks/useActivityLogger'
import { useTranslation } from '../../hooks/useTranslation'
import { registerClientInSignalSystem } from '../../services/signalClientRegistration'
import '../../components/common/SmartAccountLoader.css'

const ACCOUNTING_BACKGROUNDS = [
  {
    // Ledger / books
    gradient: 'linear-gradient(145deg, #0b3d2e 0%, #14532d 40%, #1e3a5f 100%)',
    accent: 'rgba(52, 211, 153, 0.12)',
  },
  {
    // Finance / growth
    gradient: 'linear-gradient(145deg, #0c4a6e 0%, #075985 45%, #134e4a 100%)',
    accent: 'rgba(56, 189, 248, 0.12)',
  },
  {
    // Reports / analytics
    gradient: 'linear-gradient(145deg, #1e3a5f 0%, #1e3a8a 40%, #0f766e 100%)',
    accent: 'rgba(96, 165, 250, 0.12)',
  },
  {
    // Cash / treasury
    gradient: 'linear-gradient(145deg, #134e4a 0%, #115e59 35%, #1e40af 100%)',
    accent: 'rgba(251, 191, 36, 0.1)',
  },
]

interface LoginForm {
  username: string
  password: string
  rememberMe?: boolean
}

export default function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { login } = useAuthStore()
  const setPersonalDefaults = useUserDefaultsStore((s) => s.setFromApi)
  const { language, toggleLanguage } = useLanguageStore()
  const { translate } = useTranslation()
  const { logActivity, logError } = useActivityLogger()
  const [isLoading, setIsLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [loginError, setLoginError] = useState<string | null>(null)
  const [showForgotPassword, setShowForgotPassword] = useState(false)
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('')
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false)
  const [bgIndex, setBgIndex] = useState(0)
  const isRTL = language === 'ar'
  const fromState = (location.state as any)?.from?.pathname as string | undefined
  const lastVisitedPath = localStorage.getItem('lastVisitedPath') || undefined

  useEffect(() => {
    const savedUsername = localStorage.getItem('rememberedUsername')
    if (savedUsername) {
      const formElement = document.querySelector('input[name="username"]') as HTMLInputElement
      if (formElement) {
        formElement.value = savedUsername
      }
    }
  }, [])

  const { register, handleSubmit, formState: { errors }, watch } = useForm<LoginForm>({
    defaultValues: {
      rememberMe: false,
      username: localStorage.getItem('rememberedUsername') || ''
    }
  })

  useEffect(() => {
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr'
    document.documentElement.lang = language
  }, [language, isRTL])

  // Cycle accounting-themed backgrounds
  useEffect(() => {
    const timer = setInterval(() => {
      setBgIndex((prev) => (prev + 1) % ACCOUNTING_BACKGROUNDS.length)
    }, 7000)
    return () => clearInterval(timer)
  }, [])

  const onSubmit = async (data: LoginForm) => {
    setLoginError(null)
    setIsLoading(true)

    try {
      const response = await apiClient.post(API_ENDPOINTS.auth.login, {
        username: data.username,
        password: data.password
      })

      if (data.rememberMe) {
        localStorage.setItem('rememberedUsername', data.username)
      } else {
        localStorage.removeItem('rememberedUsername')
      }

      if (response.data.token) {
        secureStorage.setItem('token', response.data.token)
      }

      const raw = response.data.user
      const userData = {
        ...raw,
        id: raw.id != null ? String(raw.id) : '',
        companyId:
          raw.companyId != null && raw.companyId !== ''
            ? String(raw.companyId)
            : undefined,
        branchId: raw.branchId != null ? String(raw.branchId) : raw.branchCode,
        assignedStoreId:
          raw.assignedStoreId != null && raw.assignedStoreId !== ''
            ? String(raw.assignedStoreId)
            : undefined,
        assignedStoreName: raw.assignedStoreName || undefined,
        lockStoreToAssigned: raw.lockStoreToAssigned === true,
        salesRepId:
          raw.salesRepId != null && Number(raw.salesRepId) > 0
            ? Number(raw.salesRepId)
            : undefined,
        salesRepName: raw.salesRepName || undefined,
        lockSalesRepToAssigned: raw.lockSalesRepToAssigned === true,
      }
      if (!userData.role && userData.roles && userData.roles.length > 0) {
        userData.role = userData.roles[0]
      } else if (!userData.role) {
        userData.role = 'User'
      }

      login(userData, response.data.token)
      setPersonalDefaults(raw.personalDefaults ?? response.data.personalDefaults)
      toast.success(translate('auth.success'))

      setTimeout(() => {
        logActivity({
          activityType: 'LOGIN',
          entityType: 'User',
          entityId: userData.id,
          actionDescription: `${translate('auth.loginActivity')} ${userData.username}`,
          isSuccess: true,
          additionalData: { username: userData.username, userId: userData.id, roles: userData.roles || [userData.role] },
        })
      }, 100)

      registerClientInSignalSystem(8080).catch(console.error)
      const targetPath = fromState || lastVisitedPath || '/dashboard'
      navigate(targetPath, { replace: true })
    } catch (error: any) {
      logError('LOGIN', 'User', error?.response?.data?.message || error.message || translate('auth.error'), {
        username: data.username,
        attemptFailed: true,
      })

      if (error.response?.data?.error === 'DeviceBlocked') {
        setLoginError(error.response.data.message || translate('auth.error'))
      } else if (error.response?.status !== 401) {
        setLoginError(translate('auth.invalidCredentials'))
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setForgotPasswordLoading(true)

    try {
      toast.success(translate('auth.forgotPasswordSent'))
      setShowForgotPassword(false)
      setForgotPasswordEmail('')
    } catch (error: any) {
      toast.error(translate('auth.forgotPasswordError'))
    } finally {
      setForgotPasswordLoading(false)
    }
  }

  const validateEmail = (value: string) => {
    if (!value) return translate('auth.usernameRequired')
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (value.includes('@') && !emailRegex.test(value)) {
      return translate('auth.emailInvalid')
    }
    return true
  }

  const floatingIcons = [
    { Icon: FiDollarSign, top: '12%', left: '8%', size: 36, delay: '0s' },
    { Icon: FiPieChart, top: '22%', left: '88%', size: 32, delay: '1.2s' },
    { Icon: FiTrendingUp, top: '68%', left: '6%', size: 34, delay: '2s' },
    { Icon: FiFileText, top: '75%', left: '90%', size: 30, delay: '0.6s' },
    { Icon: FiPercent, top: '45%', left: '92%', size: 28, delay: '1.8s' },
    { Icon: FiCreditCard, top: '85%', left: '18%', size: 30, delay: '2.5s' },
  ]

  return (
    <div
      className="min-h-screen flex items-center justify-center relative overflow-hidden"
      style={{
        fontFamily: 'Cairo, Arial, sans-serif',
        position: 'relative'
      }}
    >
      {/* Accounting-themed rotating backgrounds */}
      {ACCOUNTING_BACKGROUNDS.map((bg, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: bg.gradient,
            opacity: bgIndex === i ? 1 : 0,
            transition: 'opacity 1.4s ease-in-out',
            zIndex: 0,
          }}
        />
      ))}

      {/* Ledger grid pattern */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 1,
          pointerEvents: 'none',
          opacity: 0.18,
          backgroundImage: `
            linear-gradient(rgba(255,255,255,0.08) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.08) 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px',
        }}
      />

      {/* Soft accent glow */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 1,
          pointerEvents: 'none',
          background: `
            radial-gradient(ellipse at 15% 20%, ${ACCOUNTING_BACKGROUNDS[bgIndex].accent} 0%, transparent 45%),
            radial-gradient(ellipse at 85% 75%, rgba(255,255,255,0.06) 0%, transparent 40%)
          `,
          transition: 'background 1.4s ease-in-out',
        }}
      />

      {/* Floating accounting icons */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 1,
          pointerEvents: 'none',
          overflow: 'hidden',
        }}
      >
        {floatingIcons.map(({ Icon, top, left, size, delay }, idx) => (
          <div
            key={idx}
            style={{
              position: 'absolute',
              top,
              left,
              color: 'rgba(255,255,255,0.22)',
              animation: 'floatIcon 8s ease-in-out infinite',
              animationDelay: delay,
            }}
          >
            <Icon style={{ fontSize: size }} />
          </div>
        ))}

        {/* Bar chart decoration */}
        <svg
          width="180"
          height="100"
          viewBox="0 0 180 100"
          style={{
            position: 'absolute',
            bottom: '10%',
            right: isRTL ? 'auto' : '6%',
            left: isRTL ? '6%' : 'auto',
            opacity: 0.2,
            animation: 'floatIcon 10s ease-in-out infinite',
          }}
        >
          <rect x="10" y="50" width="22" height="50" rx="3" fill="white" />
          <rect x="45" y="30" width="22" height="70" rx="3" fill="white" />
          <rect x="80" y="15" width="22" height="85" rx="3" fill="white" />
          <rect x="115" y="40" width="22" height="60" rx="3" fill="white" />
          <rect x="150" y="25" width="22" height="75" rx="3" fill="white" />
        </svg>

        {/* Calculator outline */}
        <svg
          width="90"
          height="120"
          viewBox="0 0 90 120"
          style={{
            position: 'absolute',
            top: '18%',
            left: isRTL ? 'auto' : '5%',
            right: isRTL ? '5%' : 'auto',
            opacity: 0.18,
            animation: 'floatIcon 12s ease-in-out infinite reverse',
          }}
        >
          <rect x="5" y="5" width="80" height="110" rx="8" fill="none" stroke="white" strokeWidth="3" />
          <rect x="15" y="18" width="60" height="22" rx="4" fill="white" opacity="0.5" />
          <circle cx="28" cy="60" r="6" fill="white" />
          <circle cx="45" cy="60" r="6" fill="white" />
          <circle cx="62" cy="60" r="6" fill="white" />
          <circle cx="28" cy="80" r="6" fill="white" />
          <circle cx="45" cy="80" r="6" fill="white" />
          <circle cx="62" cy="80" r="6" fill="white" />
          <rect x="22" y="96" width="46" height="10" rx="3" fill="white" />
        </svg>
      </div>

      <style>{`
        @keyframes floatIcon {
          0%, 100% { transform: translateY(0px); opacity: 0.2; }
          50% { transform: translateY(-14px); opacity: 0.35; }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* Language Toggle */}
      <button
        onClick={toggleLanguage}
        style={{
          position: 'absolute',
          [isRTL ? 'right' : 'left']: '24px',
          top: '24px',
          padding: '8px 16px',
          backgroundColor: 'rgba(255, 255, 255, 0.15)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.25)',
          borderRadius: '8px',
          color: '#ffffff',
          fontSize: '13px',
          fontWeight: 600,
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          zIndex: 10,
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.15)'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.28)'
          e.currentTarget.style.transform = 'translateY(-2px)'
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.15)'
          e.currentTarget.style.transform = 'translateY(0)'
        }}
      >
        {language === 'ar' ? 'EN' : language === 'en' ? 'FR' : 'عربي'}
      </button>

      {/* Login Card — smaller */}
      <div
        className="w-full relative z-10"
        style={{ padding: '0 20px', maxWidth: '360px' }}
      >
        <div
          className="bg-white rounded-xl shadow-2xl"
          style={{
            padding: '28px 28px 24px',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            backdropFilter: 'blur(10px)',
            backgroundColor: 'rgba(255, 255, 255, 0.97)',
            boxShadow: '0 16px 48px rgba(0, 0, 0, 0.35)'
          }}
        >
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '16px'
              }}
            >
              <div
                className="logo-container loader-large"
                style={{
                  marginBottom: '12px',
                  width: '84px',
                  height: '84px'
                }}
              >
                <div className="rotating-ring">
                  <div className="ring-segment"></div>
                  <div className="ring-segment"></div>
                  <div className="ring-segment"></div>
                  <div className="ring-segment"></div>
                  <div className="ring-segment"></div>
                  <div className="ring-segment"></div>
                  <div className="ring-segment"></div>
                  <div className="ring-segment"></div>
                </div>
                <div className="pulse-circle"></div>
                <div className="logo-text">
                  <span className="text-smart">Smart</span>
                  <span className="text-account">Account</span>
                </div>
                <div className="sparkles">
                  <div className="sparkle sparkle-1">✨</div>
                  <div className="sparkle sparkle-2">✨</div>
                  <div className="sparkle sparkle-3">✨</div>
                  <div className="sparkle sparkle-4">✨</div>
                </div>
              </div>
            </div>

            <h1
              style={{
                fontSize: '22px',
                fontWeight: 600,
                color: '#262626',
                marginBottom: '4px',
                lineHeight: '1.3',
                textAlign: isRTL ? 'right' : 'left'
              }}
            >
              {translate('auth.welcome')}
            </h1>
            <p
              style={{
                fontSize: '13px',
                color: '#666666',
                marginTop: '2px',
                textAlign: isRTL ? 'right' : 'left'
              }}
            >
              {translate('auth.subtitle')}
            </p>
            <Link
              to="/website"
              style={{
                display: 'inline-block',
                marginTop: '10px',
                fontSize: '12px',
                fontWeight: 600,
                color: '#0f766e',
                textDecoration: 'none',
              }}
            >
              {isRTL ? 'تعرّف على خصائص النظام ←' : 'Explore product features →'}
            </Link>
          </div>

          {loginError && (
            <div
              style={{
                backgroundColor: '#fee',
                border: '1px solid #fcc',
                borderRadius: '8px',
                padding: '10px 14px',
                marginBottom: '16px',
                color: '#c00',
                fontSize: '13px',
                textAlign: isRTL ? 'right' : 'left',
                animation: 'fadeIn 0.3s ease'
              }}
            >
              {loginError}
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)}>
            <div style={{ marginBottom: '16px' }}>
              <label
                style={{
                  display: 'block',
                  fontSize: '13px',
                  fontWeight: 500,
                  color: '#333333',
                  marginBottom: '6px',
                  textAlign: isRTL ? 'right' : 'left'
                }}
              >
                {translate('auth.username')}
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  {...register('username', {
                    required: translate('auth.usernameRequired'),
                    validate: validateEmail
                  })}
                  placeholder={translate('auth.usernamePlaceholder')}
                  style={{
                    width: '100%',
                    padding: isRTL ? '11px 42px 11px 14px' : '11px 14px 11px 42px',
                    border: errors.username ? '1px solid #f56565' : '1px solid #e0e0e0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    color: '#333333',
                    backgroundColor: '#f8fafc',
                    transition: 'all 0.2s ease',
                    outline: 'none',
                    boxSizing: 'border-box',
                    boxShadow: errors.username ? '0 0 0 3px rgba(245, 101, 101, 0.1)' : 'none'
                  }}
                  onFocus={(e) => {
                    if (!errors.username) {
                      e.target.style.borderColor = '#0d9488'
                      e.target.style.boxShadow = '0 0 0 3px rgba(13, 148, 136, 0.12)'
                      e.target.style.backgroundColor = '#ffffff'
                    }
                  }}
                  onBlur={(e) => {
                    if (!errors.username) {
                      e.target.style.borderColor = '#e0e0e0'
                      e.target.style.boxShadow = 'none'
                      e.target.style.backgroundColor = '#f8fafc'
                    }
                  }}
                  dir="auto"
                />
                <FiUser
                  style={{
                    position: 'absolute',
                    [isRTL ? 'right' : 'left']: '14px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: errors.username ? '#f56565' : '#9ca3af',
                    fontSize: '18px',
                    pointerEvents: 'none',
                  }}
                />
              </div>
              {errors.username && (
                <p
                  style={{
                    marginTop: '6px',
                    fontSize: '12px',
                    color: '#ef4444',
                    textAlign: isRTL ? 'right' : 'left',
                  }}
                >
                  {errors.username.message}
                </p>
              )}
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label
                style={{
                  display: 'block',
                  fontSize: '13px',
                  fontWeight: 500,
                  color: '#333333',
                  marginBottom: '6px',
                  textAlign: isRTL ? 'right' : 'left'
                }}
              >
                {translate('auth.password')}
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  {...register('password', { required: translate('auth.passwordRequired') })}
                  placeholder={translate('auth.passwordPlaceholder')}
                  style={{
                    width: '100%',
                    padding: '11px 42px',
                    border: errors.password ? '1px solid #f56565' : '1px solid #e0e0e0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    color: '#333333',
                    backgroundColor: '#f8fafc',
                    transition: 'all 0.2s ease',
                    outline: 'none',
                    boxSizing: 'border-box',
                    boxShadow: errors.password ? '0 0 0 3px rgba(245, 101, 101, 0.1)' : 'none'
                  }}
                  onFocus={(e) => {
                    if (!errors.password) {
                      e.target.style.borderColor = '#0d9488'
                      e.target.style.boxShadow = '0 0 0 3px rgba(13, 148, 136, 0.12)'
                      e.target.style.backgroundColor = '#ffffff'
                    }
                  }}
                  onBlur={(e) => {
                    if (!errors.password) {
                      e.target.style.borderColor = '#e0e0e0'
                      e.target.style.boxShadow = 'none'
                      e.target.style.backgroundColor = '#f8fafc'
                    }
                  }}
                  dir="auto"
                />
                <FiLock
                  style={{
                    position: 'absolute',
                    [isRTL ? 'right' : 'left']: '14px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: errors.password ? '#f56565' : '#9ca3af',
                    fontSize: '18px',
                    pointerEvents: 'none',
                    zIndex: 1
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    [isRTL ? 'left' : 'right']: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    color: '#9ca3af',
                    cursor: 'pointer',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '4px',
                    zIndex: 2
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = '#0d9488'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = '#9ca3af'
                  }}
                >
                  {showPassword ? (
                    <FiEyeOff style={{ fontSize: '18px' }} />
                  ) : (
                    <FiEye style={{ fontSize: '18px' }} />
                  )}
                </button>
              </div>
              {errors.password && (
                <p
                  style={{
                    marginTop: '6px',
                    fontSize: '12px',
                    color: '#ef4444',
                    textAlign: isRTL ? 'right' : 'left',
                  }}
                >
                  {errors.password.message}
                </p>
              )}
            </div>

            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '20px',
                flexDirection: isRTL ? 'row-reverse' : 'row'
              }}
            >
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  fontSize: '13px',
                  color: '#666666',
                  gap: '8px',
                  userSelect: 'none',
                }}
              >
                <input
                  type="checkbox"
                  {...register('rememberMe')}
                  style={{
                    width: '16px',
                    height: '16px',
                    cursor: 'pointer',
                    accentColor: '#0d9488'
                  }}
                />
                <span>{translate('auth.rememberMe')}</span>
              </label>
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault()
                  setShowForgotPassword(true)
                }}
                style={{
                  fontSize: '13px',
                  color: '#666666',
                  textDecoration: 'none',
                  cursor: 'pointer',
                  fontWeight: 500
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#0d9488'
                  e.currentTarget.style.textDecoration = 'underline'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = '#666666'
                  e.currentTarget.style.textDecoration = 'none'
                }}
              >
                {translate('auth.forgotPassword')}
              </a>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              style={{
                width: '100%',
                padding: '12px 20px',
                background: isLoading
                  ? 'linear-gradient(135deg, #94a3b8 0%, #64748b 100%)'
                  : 'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '15px',
                fontWeight: 600,
                cursor: isLoading ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: isLoading
                  ? '0 2px 4px rgba(0, 0, 0, 0.1)'
                  : '0 4px 12px rgba(13, 148, 136, 0.35)',
                outline: 'none',
              }}
              onMouseEnter={(e) => {
                if (!isLoading) {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)'
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(13, 148, 136, 0.45)'
                  e.currentTarget.style.transform = 'translateY(-1px)'
                }
              }}
              onMouseLeave={(e) => {
                if (!isLoading) {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)'
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(13, 148, 136, 0.35)'
                  e.currentTarget.style.transform = 'translateY(0)'
                }
              }}
            >
              {isLoading ? translate('auth.loggingIn') : translate('auth.login')}
            </button>
          </form>
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px',
            backdropFilter: 'blur(4px)'
          }}
          onClick={() => setShowForgotPassword(false)}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              padding: '32px',
              maxWidth: '400px',
              width: '100%',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              position: 'relative'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowForgotPassword(false)}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: 'none',
                border: 'none',
                fontSize: '24px',
                color: '#9ca3af',
                cursor: 'pointer',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '6px',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f3f4f6'
                e.currentTarget.style.color = '#333333'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent'
                e.currentTarget.style.color = '#9ca3af'
              }}
            >
              ×
            </button>

            <h2
              style={{
                fontSize: '24px',
                fontWeight: 600,
                color: '#262626',
                marginBottom: '8px',
                textAlign: isRTL ? 'right' : 'left'
              }}
            >
              {translate('auth.forgotPassword')}
            </h2>
            <p
              style={{
                fontSize: '14px',
                color: '#666666',
                marginBottom: '24px',
                textAlign: isRTL ? 'right' : 'left'
              }}
            >
              {language === 'ar'
                ? 'أدخل بريدك الإلكتروني وسنرسل لك رابط إعادة تعيين كلمة المرور'
                : 'Enter your email address and we will send you a password reset link'}
            </p>

            <form onSubmit={handleForgotPassword}>
              <div style={{ marginBottom: '24px' }}>
                <label
                  style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#333333',
                    marginBottom: '8px',
                    textAlign: isRTL ? 'right' : 'left'
                  }}
                >
                  {language === 'ar' ? 'البريد الإلكتروني' : 'Email Address'}
                </label>
                <input
                  type="email"
                  value={forgotPasswordEmail}
                  onChange={(e) => setForgotPasswordEmail(e.target.value)}
                  placeholder={language === 'ar' ? 'أدخل بريدك الإلكتروني' : 'Enter your email'}
                  required
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    fontSize: '15px',
                    color: '#333333',
                    backgroundColor: '#ffffff',
                    transition: 'all 0.2s ease',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#0d9488'
                    e.target.style.boxShadow = '0 0 0 3px rgba(13, 148, 136, 0.12)'
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e0e0e0'
                    e.target.style.boxShadow = 'none'
                  }}
                  dir="auto"
                />
              </div>

              <div
                style={{
                  display: 'flex',
                  gap: '12px',
                  flexDirection: isRTL ? 'row-reverse' : 'row'
                }}
              >
                <button
                  type="button"
                  onClick={() => {
                    setShowForgotPassword(false)
                    setForgotPasswordEmail('')
                  }}
                  style={{
                    flex: 1,
                    padding: '12px 24px',
                    backgroundColor: '#f3f4f6',
                    color: '#333333',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '15px',
                    fontWeight: 500,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#e5e7eb'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#f3f4f6'
                  }}
                >
                  {language === 'ar' ? 'إلغاء' : 'Cancel'}
                </button>
                <button
                  type="submit"
                  disabled={forgotPasswordLoading}
                  style={{
                    flex: 1,
                    padding: '12px 24px',
                    backgroundColor: forgotPasswordLoading ? '#94a3b8' : '#0d9488',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '15px',
                    fontWeight: 500,
                    cursor: forgotPasswordLoading ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (!forgotPasswordLoading) {
                      e.currentTarget.style.backgroundColor = '#14b8a6'
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!forgotPasswordLoading) {
                      e.currentTarget.style.backgroundColor = '#0d9488'
                    }
                  }}
                >
                  {forgotPasswordLoading
                    ? (language === 'ar' ? 'جاري الإرسال...' : 'Sending...')
                    : (language === 'ar' ? 'إرسال' : 'Send')
                  }
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
